import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type AnalysisPanelTab = 'chart' | 'tradingview' | 'ai' | 'details' | 'manual-analysis';
export type AnalysisPanelSource = 'screening' | 'favorites';

export interface AnalysisPanelState {
  isOpen: boolean;
  selectedTicker: string;
  activeTab: AnalysisPanelTab;
  width: number; // Panel width percentage (default 40)
  source: AnalysisPanelSource; // Where the panel was opened from
}

const initialState: AnalysisPanelState = {
  isOpen: false,
  selectedTicker: '',
  activeTab: 'tradingview',
  width: 25,
  source: 'screening',
};

const analysisPanelSlice = createSlice({
  name: 'analysisPanel',
  initialState,
  reducers: {
    openPanel: (state, action: PayloadAction<{ ticker: string; tab?: AnalysisPanelTab; source?: AnalysisPanelSource }>) => {
      state.isOpen = true;
      state.selectedTicker = action.payload.ticker;
      state.source = action.payload.source || 'screening';
      // Preserve current tab if no tab is specified and panel is already open with a different ticker
      if (action.payload.tab) {
        state.activeTab = action.payload.tab;
      } else if (!state.isOpen || state.selectedTicker !== action.payload.ticker) {
        state.activeTab = state.activeTab || 'tradingview';
      }
      // If switching ticker while panel is open, preserve the current tab
    },
    closePanel: (state) => {
      state.isOpen = false;
      state.selectedTicker = '';
      // Don't reset activeTab on close - preserve it for next open
    },
    setSelectedTicker: (state, action: PayloadAction<string>) => {
      state.selectedTicker = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<AnalysisPanelTab>) => {
      state.activeTab = action.payload;
    },
    setPanelWidth: (state, action: PayloadAction<number>) => {
      // Clamp width between 15% and 40%
      state.width = Math.max(15, Math.min(40, action.payload));
    },
  },
});

export const {
  openPanel,
  closePanel,
  setSelectedTicker,
  setActiveTab,
  setPanelWidth,
} = analysisPanelSlice.actions;

export default analysisPanelSlice.reducer;